/**
 * A synthetic Muse for trying the dashboard without a headset: 4 channels at 256 Hz of 1/f-like background,
 * a 10 Hz alpha rhythm whose strength wanders slowly, and occasional blinks on AF7/AF8. Sessions recorded
 * from it are marked simulated and are never uploaded to the validation database.
 */
import { SensorClock } from "./muse/athena/clock.ts";
import { EEG_RATE, EEG_SITES, type MuseSource, type SourceSink } from "./muse/types.ts";

const BLOCK = 16; // samples per block, 62.5 ms

export class SimulatorSource implements MuseSource {
  readonly deviceKind = "muse" as const;
  readonly kind = "simulator" as const;
  readonly label = "Simulated device";
  readonly capabilities = { optics: false, battery: true };
  #timer: ReturnType<typeof setInterval> | null = null;
  #sink: SourceSink | null = null;
  #clock = new SensorClock(EEG_RATE);
  #n = 0;
  #pink = EEG_SITES.map(() => [0, 0, 0]);
  #alpha = 0.6;
  #blink = 0;
  #paused = false;
  readonly #random: () => number;

  constructor(opts: { random?: () => number } = {}) {
    this.#random = opts.random ?? Math.random;
  }

  async connect(sink: SourceSink): Promise<void> {
    this.#sink = sink;
    sink.status({ phase: "streaming", detail: "simulated EEG · 4 channels, 256 Hz", deviceName: "Simulated Muse" });
    sink.battery(87);
    this.#timer = setInterval(() => this.#emit(Date.now()), (BLOCK * 1000) / EEG_RATE);
  }

  /** Generates the next block as if it arrived at `arrivalMs` (exposed for tests). */
  emit(arrivalMs: number): void { this.#emit(arrivalMs); }

  #gauss(): number {
    const u = Math.max(1e-12, this.#random()), v = this.#random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  #emit(arrivalMs: number): void {
    const sink = this.#sink;
    if (!sink || this.#paused) return;
    const samples = EEG_SITES.map(() => new Float32Array(BLOCK));
    for (let i = 0; i < BLOCK; i++, this.#n++) {
      const t = this.#n / EEG_RATE;
      // Alpha strength: a slow random walk between "settled" and "engaged".
      this.#alpha = Math.min(1, Math.max(0.15, this.#alpha + this.#gauss() * 0.002 + (0.6 - this.#alpha) * 0.0005));
      if (this.#blink <= 0 && this.#random() < 0.25 / EEG_RATE) this.#blink = Math.round(0.3 * EEG_RATE);
      const blink = this.#blink > 0 ? 120 * Math.sin((Math.PI * this.#blink--) / (0.3 * EEG_RATE)) : 0;
      for (let c = 0; c < EEG_SITES.length; c++) {
        const p = this.#pink[c]!;
        // Three leaky integrators of white noise approximate a 1/f background.
        p[0] = 0.99 * p[0]! + this.#gauss();
        p[1] = 0.9 * p[1]! + this.#gauss();
        p[2] = 0.5 * p[2]! + this.#gauss();
        const posterior = c === 0 || c === 3;
        const alpha = (posterior ? 14 : 6) * this.#alpha * Math.sin(2 * Math.PI * 10 * t + c);
        samples[c]![i] = 0.6 * p[0]! + 1.5 * p[1]! + 2.5 * p[2]! + alpha + (posterior ? 0 : blink);
      }
    }
    const stamp = this.#clock.stamp(BLOCK, arrivalMs, 0);
    sink.eeg({ t0: stamp.t0, rate: EEG_RATE, samples, gapBefore: stamp.gapBefore });
    sink.diagnostics({ packets: this.#n / BLOCK, lastPacketAt: arrivalMs });
  }

  async halt(): Promise<void> { this.#paused = true; }
  async resume(): Promise<void> { this.#paused = false; this.#clock.reset(); }

  async disconnect(): Promise<void> {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
    this.#sink?.status({ phase: "closed", detail: "", deviceName: null });
    this.#sink = null;
  }
}
